/**
 * Prospect-demo skin registry — CLEAN / DEFAULT variant (default-deny).
 *
 * This file is the BUILD GATE seam for prospect data. The real registry — with
 * customer-facing company names and identities — lives in `demo-skins.full.ts`.
 * Demo-capable builds (`--configuration=demo` / `--configuration=sales`)
 * fileReplace this file with the full one (see angular.json `fileReplacements`).
 *
 * Every other build — ci / test / production AND any future build config —
 * gets THIS empty registry by default, so no prospect identity ever reaches a
 * real-environment artifact. `demo-flag.ts` consumes this module and degrades
 * to "no demo" against it (`getActiveDemo()` -> null); the lazy MSW chunk is
 * stubbed separately via `mock-bootstrap.ts`. A CI check greps the clean dist
 * to enforce that this seam holds.
 *
 * INVARIANT: keep this file's export surface byte-for-byte compatible with
 * `demo-skins.full.ts` — same types, same function names, same signatures.
 * Only the data (DEMO_SKINS) and the function bodies differ.
 */

/** Data templates the skin layers on. Kept identical to the full registry —
 *  including the array, which is the source the union is derived from. */
export const DEMO_BASES = ['american', 'greek', 'turkish', 'swiss', 'uk', 'lux', 'rbc'] as const;

export type DemoBase = (typeof DEMO_BASES)[number];

/** Lender hierarchy override. Kept identical to the full registry. */
export type DemoLenderType = 'super-lender' | 'standard-lender';

/** ISO 3166-1 alpha-2 codes. Kept identical to the full registry. */
export type DemoCountryCode = 'US' | 'GR' | 'TR' | 'CH' | 'GB' | 'LU' | 'CA';

/** Customer-facing identity strings. Kept identical to the full registry. */
export interface DemoSkinIdentity {
  companyName: string;
  code?: string;
  countryCode: DemoCountryCode;
  user: {
    firstName: string;
    lastName: string;
    preferredUsername?: string;
  };
}

export interface DemoSkin {
  name: string;
  base: DemoBase;
  lenderType?: DemoLenderType;
  identity: DemoSkinIdentity;
}

/** Empty in clean builds — no prospect data is bundled into real-env artifacts. */
export const DEMO_SKINS = {} satisfies Record<string, DemoSkin>;

/** A plain string in the clean build (the full build narrows this to the
 *  registry's key union). */
export type SkinName = string;

/** Mirrors the full registry's internal override shape so `setSkinOverrides`
 *  keeps the same signature for callers compiled against this clean variant. */
type RawOverrides = Record<
  string,
  {
    base?: DemoBase;
    companyName?: string;
    code?: string;
    countryCode?: DemoCountryCode;
    lenderType?: DemoLenderType;
    user?: Partial<DemoSkinIdentity['user']>;
  }
>;

/** No registered skins in a clean build. */
export function listSkinNames(): string[] {
  return [];
}

/** No skin resolves in a clean build. */
export function getSkin(_name: string | null | undefined): DemoSkin | null {
  return null;
}

/** No-op — overrides are a demo-only affordance. */
export function setSkinOverrides(_name: string, _overrides: RawOverrides[string] | null): void {
  // intentionally empty in clean builds
}

/** No-op — ad-hoc skins are a demo-only affordance. */
export function setAdhocSkin(_skin: DemoSkin): void {
  // intentionally empty in clean builds
}

/** No-op — ad-hoc skins are a demo-only affordance. */
export function deleteAdhocSkin(_name: string): void {
  // intentionally empty in clean builds
}

/** No ad-hoc skins exist in a clean build. */
export function isAdhocSkin(_name: string): boolean {
  return false;
}

/** Matches the full semantics for the rare clean-build caller that holds a
 *  skin object (none do today, but keep behaviour identical to be safe). */
export function effectiveLenderType(skin: DemoSkin): DemoLenderType {
  if (skin?.lenderType) return skin.lenderType;
  return skin?.base === 'american' ? 'super-lender' : 'standard-lender';
}

export function getCode(skin: DemoSkin): string {
  // Mirror demo-skins.full.ts: prefer an explicit code, else derive from the
  // first word of companyName (upper, capped at 6) — keeps the stub/full seam
  // export-compatible so only DATA differs, not behaviour.
  if (skin?.identity?.code) return skin.identity.code;
  const firstWord = skin?.identity?.companyName?.trim().split(' ')[0];
  return (firstWord || skin?.name || '').slice(0, 6).toUpperCase();
}

export function getEntityLabel(_skin: DemoSkin): string {
  return '';
}

export function getUserEmail(_skin: DemoSkin): string {
  return '';
}
